<template>
    <div class="aq-chatbot">
        <!-- Floating chat button -->
        <button
            class="aq-chatbot__fab"
            :class="{ 'aq-chatbot__fab--open': isOpen }"
            @click="toggleChat"
            :aria-label="isOpen ? t('chatbot.close') : t('chatbot.open')"
        >
            <img
                v-if="!isOpen"
                src="@/public/assets/images/aquassist-icon.svg"
                alt="Aquassist"
                class="aq-chatbot__fab-icon"
            />
            <v-icon v-else color="white" size="28">mdi-close</v-icon>
            <span v-if="hasInsight && !isOpen" class="aq-chatbot__fab-badge">
                <v-icon size="16" color="#fff">mdi-lightbulb-on</v-icon>
            </span>
        </button>

        <!-- Chat window -->
        <Transition name="aq-chatbot-slide">
            <div v-if="isOpen" class="aq-chatbot__window">
                <!-- Header -->
                <div class="aq-chatbot__header">
                    <div class="aq-chatbot__header-left">
                        <img
                            src="@/public/assets/images/aquassist-icon.svg"
                            alt="Aquassist"
                            class="aq-chatbot__header-logo"
                        />
                        <div class="aq-chatbot__header-info">
                            <span class="aq-chatbot__header-title">Aquassist GPT</span>
                            <span class="aq-chatbot__header-subtitle">{{
                                t('chatbot.replyMessage')
                            }}</span>
                        </div>
                    </div>
                    <div class="aq-chatbot__header-pill">
                        <v-icon size="14" color="white">mdi-chat-processing-outline</v-icon>
                        <span>Chat</span>
                    </div>
                    <button class="aq-chatbot__header-close" @click="toggleChat">
                        <v-icon color="white" size="18">mdi-minus</v-icon>
                    </button>
                </div>

                <!-- Beta banner -->
                <div class="aq-chatbot__beta">
                    <v-icon size="14" color="#013067">mdi-information-outline</v-icon>
                    <span>{{ t('chatbot.betaMessage') }}</span>
                </div>

                <!-- Mode toggle -->
                <div class="aq-chatbot__mode-toggle">
                    <button
                        class="aq-chatbot__mode-btn"
                        :class="{ 'aq-chatbot__mode-btn--active': chatMode === 'kb' }"
                        @click="chatMode = 'kb'"
                    >
                        <v-icon size="14">mdi-book-search-outline</v-icon>
                        {{ t('chatbot.modeKB') }}
                    </button>
                    <button
                        class="aq-chatbot__mode-btn"
                        :class="{ 'aq-chatbot__mode-btn--active': chatMode === 'standard' }"
                        @click="chatMode = 'standard'"
                    >
                        <v-icon size="14">mdi-robot-outline</v-icon>
                        {{ t('chatbot.modeStandard') }}
                    </button>
                </div>

                <!-- Messages area -->
                <div ref="messagesContainer" class="aq-chatbot__messages">
                    <!-- Initial greeting -->
                    <div class="aq-chatbot__message aq-chatbot__message--bot">
                        <img
                            src="@/public/assets/images/aquassist-icon.svg"
                            alt="AI"
                            class="aq-chatbot__message-avatar"
                        />
                        <div class="aq-chatbot__message-content">
                            <span class="aq-chatbot__message-name">Aquassist support</span>
                            <div class="aq-chatbot__message-bubble aq-chatbot__message-bubble--bot">
                                {{ t('chatbot.initialMessage') }}
                            </div>
                        </div>
                    </div>

                    <!-- Conversation messages -->
                    <template v-for="(msg, index) in messages" :key="index">
                        <div
                            class="aq-chatbot__message"
                            :class="{
                                'aq-chatbot__message--user': msg.role === 'user',
                                'aq-chatbot__message--bot': msg.role === 'bot',
                            }"
                        >
                            <img
                                v-if="msg.role === 'bot'"
                                src="@/public/assets/images/aquassist-icon.svg"
                                alt="AI"
                                class="aq-chatbot__message-avatar"
                            />
                            <div class="aq-chatbot__message-content">
                                <span v-if="msg.role === 'bot'" class="aq-chatbot__message-name"
                                    >Aquassist support</span
                                >
                                <div
                                    class="aq-chatbot__message-bubble"
                                    :class="{
                                        'aq-chatbot__message-bubble--user': msg.role === 'user',
                                        'aq-chatbot__message-bubble--bot': msg.role === 'bot',
                                        'aq-chatbot__message-bubble--insight':
                                            msg.role === 'bot' && msg.isInsight,
                                    }"
                                >
                                    <div v-if="msg.isInsight" class="aq-chatbot__insight-label">
                                        <v-icon size="14" color="#f9a825">mdi-lightbulb-on</v-icon>
                                        {{ t('chatbot.insightLabel') }}
                                    </div>
                                    {{ msg.text }}
                                    <div
                                        v-if="msg.sources && msg.sources.length"
                                        class="aq-chatbot__sources"
                                    >
                                        <div class="aq-chatbot__sources-title">
                                            <v-icon size="12" color="#2b3b4f"
                                                >mdi-file-document-outline</v-icon
                                            >
                                            {{ t('chatbot.sources') }}
                                        </div>
                                        <div
                                            v-for="(src, sIdx) in msg.sources"
                                            :key="sIdx"
                                            class="aq-chatbot__source-item"
                                        >
                                            {{ src.name }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- Typing indicator -->
                    <div v-if="isLoading" class="aq-chatbot__message aq-chatbot__message--bot">
                        <img
                            src="@/public/assets/images/aquassist-icon.svg"
                            alt="AI"
                            class="aq-chatbot__message-avatar"
                        />
                        <div class="aq-chatbot__message-content">
                            <span class="aq-chatbot__message-name">Aquassist support</span>
                            <div class="aq-chatbot__message-bubble aq-chatbot__message-bubble--bot">
                                <div class="aq-chatbot__typing">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Input area -->
                <div class="aq-chatbot__input-area">
                    <input
                        ref="chatInput"
                        v-model="inputText"
                        class="aq-chatbot__input"
                        :placeholder="t('chatbot.typeMessage')"
                        :disabled="isLoading"
                        @keydown.enter.prevent="sendMessage"
                    />
                    <button
                        class="aq-chatbot__send"
                        :disabled="!inputText.trim() || isLoading"
                        @click="sendMessage"
                    >
                        <v-icon
                            :color="inputText.trim() && !isLoading ? '#013067' : '#9E9E9E'"
                            size="22"
                            >mdi-send</v-icon
                        >
                    </button>
                </div>
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">
    import { ref, nextTick, watch, onMounted } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { getSocketClient } from '@/utilities/socket';

    interface ChatSource {
        name: string;
        uri: string;
    }

    interface ChatMessage {
        role: 'user' | 'bot';
        text: string;
        sources?: ChatSource[];
        isInsight?: boolean;
    }

    const { t } = useI18n();

    const isOpen = ref(false);
    const inputText = ref('');
    const isLoading = ref(false);
    const messages = ref<ChatMessage[]>([]);
    const sessionId = ref<string | null>(null);
    const messagesContainer = ref<HTMLElement | null>(null);
    const chatInput = ref<HTMLInputElement | null>(null);
    const chatMode = ref<'kb' | 'standard'>('kb');
    const hasInsight = ref(false);

    const scrollToBottom = async () => {
        await nextTick();
        if (messagesContainer.value) {
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        }
    };

    const toggleChat = () => {
        isOpen.value = !isOpen.value;

        if (!isOpen.value) {
            sessionId.value = null;
        }

        if (isOpen.value) {
            hasInsight.value = false;
            nextTick(() => {
                chatInput.value?.focus();
                scrollToBottom();
            });
        }
    };

    const sendMessage = () => {
        const text = inputText.value.trim();
        if (!text || isLoading.value) return;

        messages.value.push({ role: 'user', text });
        inputText.value = '';
        isLoading.value = true;
        scrollToBottom();

        const socket = getSocketClient();
        if (!socket) {
            messages.value.push({
                role: 'bot',
                text: t('chatbot.connectionError'),
            });
            isLoading.value = false;
            scrollToBottom();
            return;
        }

        if (chatMode.value === 'kb') {
            socket.emit('GPT_CHAT_MESSAGE', {
                question: text,
                sessionId: sessionId.value,
            });
        } else {
            socket.emit('GPT_DIRECT_MESSAGE', {
                question: text,
            });
        }
    };

    const setupSocketListeners = () => {
        const socket = getSocketClient();
        if (!socket) return;

        socket.on(
            'GPT_CHAT_RESPONSE',
            (data: { text: string; sessionId: string; sources?: ChatSource[]; done: boolean }) => {
                isLoading.value = false;
                if (data.sessionId) {
                    sessionId.value = data.sessionId;
                }
                messages.value.push({
                    role: 'bot',
                    text: data.text,
                    sources: data.sources?.length ? data.sources : undefined,
                });
                scrollToBottom();
            },
        );

        socket.on('GPT_CHAT_ERROR', (data: { error: string }) => {
            isLoading.value = false;

            const errMsg = data.error || t('chatbot.genericError');

            if (errMsg.includes('Knowledge base configurations cannot be modified')) {
                sessionId.value = null;
            }

            messages.value.push({ role: 'bot', text: errMsg });
            scrollToBottom();
        });
    };

    const setupInsightListener = () => {
        const socket = getSocketClient();
        if (!socket) return;

        socket.on('GPT_INSIGHT', (data: { text: string; context: string }) => {
            hasInsight.value = true;
            messages.value.push({
                role: 'bot',
                text: data.text,
                isInsight: true,
            });
            if (isOpen.value) {
                hasInsight.value = false;
                scrollToBottom();
            }
        });
    };

    onMounted(() => {
        setupInsightListener();
    });

    watch(
        isOpen,
        (newVal) => {
            if (newVal) {
                setupSocketListeners();
            }
        },
        { once: true },
    );
</script>
